import { useState } from "react";
import { Star, Loader2, Gift, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { copyText } from "@/lib/qr";
import { toast } from "sonner";

export function Stars({ value, size = "h-4 w-4", onPick, testId }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const icon = <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />;
        return onPick ? <button key={n} type="button" onClick={() => onPick(n)} className="p-1 transition-transform hover:scale-110" data-testid={`rating-star-${n}`}>{icon}</button> : <span key={n}>{icon}</span>;
      })}
    </span>
  );
}

export function CouponReward({ c, slug, color }) {
  const copy = async () => { await copyText(c.code); toast.success("Cupom copiado"); };
  return (
    <div className="rounded-xl border-2 border-dashed p-4 text-center space-y-2" style={{ borderColor: color }} data-testid="thanks-coupon">
      <p className="flex items-center justify-center gap-1 text-sm font-semibold"><Gift className="h-4 w-4" style={{ color }} />Presente para o seu próximo pedido</p>
      <p className="font-mono text-2xl font-extrabold tracking-widest" data-testid="thanks-coupon-code">{c.code}</p>
      <p className="text-sm" data-testid="thanks-coupon-label">{c.label} · válido até {fmtDate(c.expires_at)}{c.used ? " · já usado" : ""}</p>
      {!c.used && (
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={copy} data-testid="thanks-coupon-copy"><Copy className="h-4 w-4 mr-1" />Copiar</Button>
          <Button asChild className="flex-1 text-white" style={{ background: color }}><a href={`/cardapio/${slug}?modo=delivery&cupom=${c.code}`} data-testid="thanks-coupon-use">Usar agora</a></Button>
        </div>
      )}
    </div>
  );
}

export function RatingBox({ slug, id, rating, coupon, onRated, color }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (rating) return (
    <div className="space-y-3">
      <div className="rounded-xl bg-amber-50 p-3 text-center space-y-1" data-testid="rating-done">
        <p className="text-sm font-semibold">Obrigado pela sua avaliação!</p>
        <Stars value={rating.stars} />
        {rating.comment && <p className="text-xs text-slate-500">"{rating.comment}"</p>}
      </div>
      {rating.reply && (
        <div className="rounded-xl border-l-4 bg-slate-50 p-3 text-left text-sm" style={{ borderColor: color }} data-testid="rating-reply">
          <p className="text-xs font-semibold" style={{ color }}>Resposta da loja · {fmtDateTime(rating.reply.at)}</p>
          <p className="mt-1 text-slate-700">{rating.reply.text}</p>
        </div>
      )}
      {coupon && <CouponReward c={coupon} slug={slug} color={color} />}
    </div>
  );
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/public/menu/${slug}/orders/${id}/rating`, { stars, comment }); onRated(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-4 space-y-3 text-center" data-testid="rating-box">
      <p className="font-semibold">Como foi o seu pedido?</p>
      <Stars value={stars} size="h-8 w-8" onPick={setStars} />
      {stars > 0 && <Textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))} rows={2} placeholder="Conte o que achou (opcional)" className="bg-white" data-testid="rating-comment" />}
      <Button className="w-full text-white" style={{ background: color }} disabled={!stars || busy} onClick={send} data-testid="rating-send-btn">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enviar avaliação
      </Button>
    </div>
  );
}
